import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchJson, postJson } from '../api';
import PollTiles from '../components/PollTiles';
import { AskingHeader, applyTallies, optimisticVote, votesLine } from '../components/PollView';
import StoreBadges from '../components/StoreBadges';
import Toast, { useToast } from '../components/Toast';
import { ShareIcon } from '../components/Icons';
import { useAuth } from '../lib/auth';
import { VotoLetters } from '../components/Chrome';
import { trackSharePageView, trackVote } from '../lib/analytics';
import useLivePoll from '../lib/useLivePoll';
import { displayUrl, sharePoll } from '../lib/share';
import { readVoterName, saveVoterName, wasVoterNameAsked } from '../lib/voterName';

// "Voting as ___ (optional)" — asked once, then shown as "Voting as Eric · change".
function VotingAs({ name, editing, draft, setDraft, onSave, onEdit }) {
  if (editing) {
    return (
      <form
        className="flex items-center gap-2 rounded-xl bg-voto-surface px-3.5 py-2 ring-1 ring-voto-line"
        onSubmit={(e) => { e.preventDefault(); onSave(draft); }}
      >
        <label htmlFor="voter-name" className="flex-none text-sm text-voto-fg">Voting as</label>
        <input
          id="voter-name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (draft.trim()) onSave(draft); }}
          maxLength={40}
          autoComplete="given-name"
          placeholder="your name"
          className="min-w-0 flex-1 rounded-md bg-transparent px-1 py-1 text-sm font-semibold text-voto-fg outline-none placeholder:font-normal placeholder:text-voto-muted focus-visible:ring-1 focus-visible:ring-voto-coral"
        />
        <span className="flex-none text-xs text-voto-muted">(optional)</span>
      </form>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-xl bg-voto-surface px-3.5 py-2.5 text-sm ring-1 ring-voto-line">
      <span className="text-voto-fg">
        Voting as <b className="font-semibold">{name || 'someone'}</b>
      </span>
      <button type="button" onClick={onEdit} className="rounded font-semibold text-voto-coral hover:underline">
        change
      </button>
    </div>
  );
}

function SoftFooter() {
  return (
    <div className="rounded-2xl bg-voto-surface p-4 ring-1 ring-voto-line">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-voto-muted">
        <span className="text-lg font-bold tracking-[-0.02em] text-white" aria-label="voto" role="img"><VotoLetters moto={false} /></span>
        <span>Ask your own group something</span>
        <span aria-hidden="true">·</span>
        <span className="font-semibold text-voto-coral">Get the app</span>
      </p>
      <StoreBadges className="mt-3" size="h-10" />
    </div>
  );
}

/**
 * The shared poll page (spec §1 "Share loop"): phone-first, no sign-in, no app
 * wall. Tap a photo to vote (source "link", with the optional name); results
 * reveal at once with names and keep updating every 10 s while visible.
 */
export default function SharedPollPage() {
  const { slug } = useParams();
  const auth = useAuth();
  const user = auth?.user || null;
  const [poll, setPoll] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | missing | error
  const [voting, setVoting] = useState(false);
  const [voteError, setVoteError] = useState('');
  const [name, setName] = useState(() => readVoterName());
  const [editingName, setEditingName] = useState(() => !wasVoterNameAsked());
  const [draft, setDraft] = useState(() => readVoterName());
  const [toast, showToast] = useToast();
  const latest = useRef(0);

  const load = useCallback(async (initial = false) => {
    const ticket = ++latest.current;
    try {
      const data = await fetchJson(`/p/${encodeURIComponent(slug)}`);
      if (ticket !== latest.current) return;
      setPoll((current) => {
        // Keep our own pick if a refresh races the vote write.
        if (current && current.voted_item_id != null && data.voted_item_id == null) {
          return { ...data, voted_item_id: current.voted_item_id };
        }
        return data;
      });
      setStatus('ready');
      if (initial) trackSharePageView({ slug, poll_id: data.id });
    } catch (err) {
      if (!initial) return; // a failed live refresh keeps what we have
      setStatus(err.status === 404 ? 'missing' : 'error');
    }
  }, [slug]);

  useEffect(() => { setStatus('loading'); load(true); }, [load]);

  const isMine = !!user && poll?.author_id != null && poll.author_id === user.id;
  const revealed = !!poll && (poll.voted_item_id != null || isMine);
  useLivePoll(status === 'ready' && revealed, () => load(false), 10000);

  function saveName(value) {
    const clean = saveVoterName(value);
    setName(clean);
    setDraft(clean);
    setEditingName(false);
    return clean;
  }

  async function vote(item) {
    if (!poll || voting || revealed) return;
    // The name prompt is asked once: whatever is in it now (even empty) is the answer.
    const voterName = editingName ? saveName(draft) : name;
    setVoting(true);
    setVoteError('');
    const before = poll;
    setPoll(optimisticVote(poll, item));
    try {
      const body = { item_id: item.id, source: 'link' };
      if (voterName) body.voter_name = voterName;
      const data = await postJson(`/polls/${poll.id}/vote`, body);
      trackVote('link', { poll_id: poll.id, named: !!voterName });
      setPoll((current) => applyTallies(current, data, item.id));
      load(false); // pick up the names (voters_by_item) right away
    } catch (err) {
      if (err.status === 409 || err.status === 403) {
        setPoll({ ...before, voted_item_id: before.voted_item_id ?? item.id });
        if (err.status === 409) setVoteError('You already voted on this one.');
        load(false);
      } else {
        setPoll(before);
        setVoteError('Couldn’t record your vote. Try again.');
      }
    } finally {
      setVoting(false);
    }
  }

  async function share() {
    try {
      const { url, channel } = await sharePoll(poll, { where: 'share_page' });
      if (channel === 'copy') showToast(`Link copied · ${displayUrl(url)}`);
    } catch (_) {
      showToast('Sharing isn’t available here.');
    }
  }

  return (
    <div className="min-h-screen bg-voto-bg text-voto-fg">
      <main className="mx-auto flex w-full max-w-md flex-col gap-3 px-4 pb-10 pt-5">
        {status === 'loading' && <p className="py-16 text-center text-voto-muted">Loading the poll…</p>}

        {(status === 'missing' || status === 'error') && (
          <div className="py-12 text-center">
            <p className="text-lg font-semibold">
              {status === 'missing' ? 'This poll isn’t available.' : 'We couldn’t load this poll.'}
            </p>
            <p className="mt-2 text-sm text-voto-muted">
              {status === 'missing' ? 'It may have been removed, or the link is mistyped.' : 'Check your connection and try again.'}
            </p>
          </div>
        )}

        {status === 'ready' && poll && (
          <>
            <AskingHeader
              poll={poll}
              sub={revealed ? `${votesLine(poll)} so far` : 'Tap a photo to vote · no sign-in'}
            />
            <h1 className="text-[22px] font-semibold leading-tight">{poll.caption}</h1>
            <PollTiles
              src={poll.image_url}
              items={poll.items}
              count={poll.items_count || poll.items?.length}
              alt={poll.caption || 'Poll image'}
              variant="rounded"
              onVote={revealed ? null : vote}
              revealed={revealed}
              votedItemId={poll.voted_item_id ?? null}
              totalVotes={poll.votes_count || 0}
              voting={voting}
              votersByItem={poll.voters_by_item || null}
            />
            {voteError && <p className="text-sm text-voto-coral">{voteError}</p>}
            {isMine && (
              <p className="text-sm text-voto-muted">
                This is your poll.{' '}
                <Link to={`/poll/${poll.id}`} className="font-semibold text-voto-coral hover:underline">See who voted</Link>
              </p>
            )}
            {!isMine && (
              <VotingAs
                name={name}
                editing={editingName}
                draft={draft}
                setDraft={setDraft}
                onSave={saveName}
                onEdit={() => { setDraft(name); setEditingName(true); }}
              />
            )}
            <div className="flex items-center justify-between px-1 text-[13px]">
              <span className="flex items-center gap-2 text-voto-muted">
                {revealed ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-voto-green" aria-hidden="true" />
                    Results update live
                  </>
                ) : (
                  'Results show after you vote'
                )}
              </span>
              <button type="button" onClick={share} className="flex items-center gap-1.5 rounded-md px-1 py-1 font-semibold text-voto-coral hover:underline">
                <ShareIcon />
                Share
              </button>
            </div>
            <Toast message={toast} />
          </>
        )}

        {status !== 'loading' && (
          <div className="mt-6">
            <SoftFooter />
          </div>
        )}
      </main>
    </div>
  );
}
